import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  DeviceType,
  InventoryListItem,
  InventoryShippingType,
  FulfillmentOrderType,
  OrganizationModelBundleType,
  FulfillmentOrderLogType,
  OrderLogType,
  InventoryDeviceDetailsType,
  NonSerializedItemType,
  AddNonSerializedItemsToInventoryDto,
  NonSerializedItemLogType,
  AccessoryBundleType,
  DeviceStatus,
} from '../../types/inventory/inventory.types';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { setModalContent, setShowModal } from '../utils/utils.slice';
import { additionalQueryParams } from '../../utils/utilities';
import {
  AcsAtaVirtualDeviceType,
  AcsAtaVirtualOverrideType,
  AcsAtaVirtualPortType,
  CommentType,
  CreateOrderCommentDto,
  DeviceCategory,
} from '../../types/order/order.type';
import { ACSATAConfigType } from '../../types/ata/acs-ata/acs-ata-config/acs-ata-config.type';
import { getNewOrderDetails, getOrderDetails } from '../orders/orders.action';
import { VirtualDialerType, VirtualPowerCycleType, VirtualSimType } from '../../types/device/device.type';
import { PhoneNumberType } from '../../types/other';
import React from 'react';
import MoveDeviceSummary from '../../components/Modal/MoveDeviceSummary/MoveDeviceSummary';
import { AcsFirmwareType } from '../../types/ata/acs-ata/acs-firmware/acs-firmware.type';
import { ATALogType } from '../../types/ata/ata-log/ata-log.type';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { setInventoryDeviceDetails } from './inventory.slice';

export const postAddATAsToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{
        serialNumber: string;
        description: string;
        mac: string;
        factoryPassword: string | null;
        imei: string | null;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddATAsToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-ata`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add ATAs Summary',
          successLabel: 'ATAs were successfully added:',
          errorLabel: 'ATAs that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const sendInventoryATACommand = createAsyncThunk<
  ATALogType,
  {
    id: number;
    fetchList: boolean;
    data: {
      command?: string;
      task_type: string;
      commands?: Array<{ name: string; poe1?: string; poe2?: string; ups?: string; url?: string }>;
    };
  },
  { state: RootReducerState }
>('inventory/sendInventoryATACommand', async ({ id, data, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<ATALogType>(
      `${process.env.REACT_APP_API_URL}/inventory/acs-ata-device/${id}/command`,
      data
    );
    if (fetchList) {
      // thunkAPI.dispatch(getATAsList(thunkAPI.getState().atasReducer.atasRouteParams));
    } else {
      // thunkAPI.dispatch(getATADetails({ id, type }));
    }

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending command!' });
  }
});

export const factoryResetInventoryATA = createAsyncThunk<
  ATALogType,
  {
    id: number;
    fetchList: boolean;
  },
  { state: RootReducerState }
>('inventory/factoryResetInventoryATA', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<ATALogType>(
      `${process.env.REACT_APP_API_URL}/inventory/acs-ata-device/${id}/factory-reset`
    );

    if (fetchList) {
      // thunkAPI.dispatch(getATAsList(thunkAPI.getState().atasReducer.atasRouteParams));
    } else {
      // thunkAPI.dispatch(getATADetails({ id, type }));
    }

    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during sending command!' });
  }
});

export const assignDevicesToOrderVirtualDevices = createAsyncThunk<
  { success: boolean },
  {
    data: { deviceId: number; vid: string };
  },
  { state: RootReducerState }
>('inventory/assignDevicesToOrderVirtualDevices/no-loader', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/virtual-device/assign-device`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during assigning device!' });
  }
});

export const unassignDevicesToOrderVirtualDevices = createAsyncThunk<
  { success: boolean },
  {
    data: { vid: string };
  },
  { state: RootReducerState }
>('inventory/unassignDevicesToOrderVirtualDevices', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/virtual-device/unassign-device`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during unassigning device!' });
  }
});

export const postAddSIMsToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{
        description: string | null;
        serialNumber: string;
        iccid: string;
        eid: string;
        mobile: { number: string; countryCode: string } | null;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddSIMsToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-sim`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add SIMs Summary',
          successLabel: 'SIMs were successfully added:',
          errorLabel: 'SIMs that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddSmartphonesToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{
        description: string | null;
        serialNumber: string;
        imei: string | null;
        imei2: string | null;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddSmartphonesToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-smartphone`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Smartphones Summary',
          successLabel: 'Smartphones were successfully added:',
          errorLabel: 'Smartphones that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddPowerCyclesToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{
        description: string | null;
        serialNumber: string;
        password: string;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddPowerCyclesToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-power-cycle`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Power Cycles Summary',
          successLabel: 'Power Cycles were successfully added:',
          errorLabel: 'Power Cycles that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddRoutersToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{ description: string | null; serialNumber: string; imei: string }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddRoutersToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-router`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Routers Summary',
          successLabel: 'Routers were successfully added:',
          errorLabel: 'Routers that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddBatteriesToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{ description: string | null; serialNumber: string }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddBatteriesToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-battery`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Batteries Summary',
          successLabel: 'Batteries were successfully added:',
          errorLabel: 'Batteries that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddEnclosuresToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{ description: string | null; serialNumber: string }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddEnclosuresToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-enclosure`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Enclosures Summary',
          successLabel: 'Enclosures were successfully added:',
          errorLabel: 'Enclosures that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const postAddDialersToInventory = createAsyncThunk<
  { success: Array<string>; errors: Array<string> },
  {
    data: {
      owner?: { id: number };
      warehouse: { id: number };
      model: { id: number };
      devices: Array<{
        description: string | null;
        serialNumber: string;
        csAccount: string;
        dialerType: { id: number; displayName: string };
        csPrimaryReceiver: { number: string; countryCode: string } | null;
        csSecondaryReceiver: { number: string; countryCode: string } | null;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAddDialersToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{ success: Array<string>; errors: Array<string> }>(
      `${process.env.REACT_APP_API_URL}/inventory/add-dialer`,
      data
    );

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(
        getInventoryList({ warehouse_id: thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id })
      );
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
          title: 'Add Dialers Summary',
          successLabel: 'Dialers were successfully added:',
          errorLabel: 'Dialers that could not be added:',
          buttonColor: 'info',
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding inventory items!' });
  }
});

export const getInventoryList = createAsyncThunk<
  {
    atas: Array<InventoryListItem>;
    batteries: Array<InventoryListItem>;
    dialers: Array<InventoryListItem>;
    enclosures: Array<InventoryListItem>;
    powerCycles: Array<InventoryListItem>;
    routers: Array<InventoryListItem>;
    sims: Array<InventoryListItem>;
  },
  { warehouse_id?: number; org_id?: number; forSale?: boolean },
  { state: RootReducerState }
>('inventory/getInventoryList', async (data, thunkAPI) => {
  try {
    const { ...params } = data;

    const queryParams = { ...params };
    if (queryParams.forSale === false) {
      delete queryParams.forSale;
    }

    const queryString = Object.keys(queryParams)
      .map(key => key + '=' + queryParams[key])
      .join('&');

    const response = await axios.get<{
      atas: Array<InventoryListItem>;
      batteries: Array<InventoryListItem>;
      dialers: Array<InventoryListItem>;
      enclosures: Array<InventoryListItem>;
      powerCycles: Array<InventoryListItem>;
      routers: Array<InventoryListItem>;
      sims: Array<InventoryListItem>;
    }>(`${process.env.REACT_APP_API_URL}/inventory/get-inventory${queryString ? `?${queryString}` : ''}`);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching inventory!' });
  }
});

export const getDevicesList = createAsyncThunk<
  PaginationType<DeviceType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('inventory/getDevicesList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      undefined,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams: Record<string, unknown> = { ...params, ...additionalParams };
    if (
      Object.prototype.hasOwnProperty.call(mergedParams, 'status') &&
      (mergedParams.status === undefined ||
        mergedParams.status === null ||
        mergedParams.status === '' ||
        mergedParams.status === false)
    )
      delete mergedParams.status;
    if (
      Object.prototype.hasOwnProperty.call(mergedParams, 'search') &&
      (mergedParams.search === undefined ||
        mergedParams.search === null ||
        mergedParams.search === '' ||
        mergedParams.search === false)
    )
      delete mergedParams.search;
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + String(mergedParams[key]))
      .join('&');

    const response = await axios.get<PaginationType<DeviceType>>(
      `${process.env.REACT_APP_API_URL}/inventory/get-devices${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching devices!' });
  }
});

export const getAtaConfigurationsList = createAsyncThunk<
  Array<ACSATAConfigType>,
  { type: 'POTSCAST' | 'ACS'; model?: string },
  {
    state: RootReducerState;
  }
>('inventory/getAtaConfigurationsList/no-loader', async ({ type, model }, thunkAPI) => {
  try {
    const response = await axios.get<Array<ACSATAConfigType>>(`${process.env.REACT_APP_API_URL}/acs/config`, {
      params: { type, model },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching configuration!' });
  }
});

export const getVirtualDeviceDetails = createAsyncThunk<
  AcsAtaVirtualDeviceType,
  { id: string },
  { state: RootReducerState }
>('inventory/getVirtualDeviceDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<AcsAtaVirtualDeviceType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching device!' });
  }
});

export const patchDevicesForSaleFlag = createAsyncThunk<
  { success: boolean },
  {
    devices: Array<{
      id: number;
      availableForSale: boolean;
    }>;
  },
  { state: RootReducerState }
>('inventory/patchDevicesForSaleFlag', async ({ devices }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(`${process.env.REACT_APP_API_URL}/inventory/devices`, {
      devices,
    });

    thunkAPI.dispatch(getDevicesList(thunkAPI.getState().inventoryReducer.devicesRouteParams));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching device!' });
  }
});

export const patchVirtualAtaAcs = createAsyncThunk<
  AcsAtaVirtualDeviceType,
  {
    id: number;
    dto: {
      model?: { id: number };
      configOverrides?: Array<AcsAtaVirtualOverrideType>;
      ports?: Array<{
        id: number;
        dto: {
          enabled: boolean;
          profile: { id: number; displayName: string } | null;
          pbx: { id: number; displayName: string } | null;
          pbxExtension: { id: number; displayName: string } | null;
        };
      }>;
    };
    orderId?: number;
    vid?: string;
  },
  { state: RootReducerState }
>('inventory/patchVirtualAtaAcs', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<AcsAtaVirtualDeviceType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/virtual-ata-acs`,
      data
    );

    if (data.orderId) await thunkAPI.dispatch(getNewOrderDetails({ id: data.orderId }));
    if (data.vid) await thunkAPI.dispatch(getVirtualDeviceDetails({ id: data.vid }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating device!' });
  }
});

export const patchVirtualAtaAcsPort = createAsyncThunk<
  AcsAtaVirtualPortType,
  { id: number; dto: { portConfigOverrides: Array<AcsAtaVirtualOverrideType> } },
  { state: RootReducerState }
>('inventory/patchVirtualAtaAcsPort', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<AcsAtaVirtualPortType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/virtual-ata-acs-port`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating port device!' });
  }
});

export const patchVirtualPowerCycle = createAsyncThunk<
  VirtualPowerCycleType,
  {
    id: number;
    dto: {
      id: number;
      password?: string | null;
      channels?: Array<{
        id: number;
        deviceName?: string | null;
        enableChannel?: boolean;
        virtualDeviceId?: string | null;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/patchVirtualPowerCyclePort', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<VirtualPowerCycleType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/virtual-power-cycle`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating powerCycle!' });
  }
});

export const patchVirtualSim = createAsyncThunk<
  VirtualSimType,
  {
    id: number;
    dto: {
      router: { id: number } | null;
      simSlot: number | null;
    };
  },
  { state: RootReducerState }
>('inventory/patchVirtualSim', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<VirtualSimType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/virtual-sim`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating device!' });
  }
});

export const patchVirtualDialer = createAsyncThunk<
  VirtualDialerType,
  {
    id: number;
    dto: {
      csAccount: string | null;
      csPrimaryReceiver: PhoneNumberType | null;
      csSecondaryReceiver: PhoneNumberType | null;
      configKey: string | null;
    };
  },
  { state: RootReducerState }
>('inventory/patchVirtualDialer', async (data, thunkAPI) => {
  try {
    const response = await axios.patch<VirtualDialerType>(
      `${process.env.REACT_APP_API_URL}/inventory/virtual-device/virtual-dialer`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating device!' });
  }
});

export const getAtaPortConfigurationsList = createAsyncThunk<
  Array<ACSATAConfigType>,
  { type: 'POTSCAST' | 'ACS' },
  {
    state: RootReducerState;
  }
>('inventory/getAtaPortConfigurationsList/no-loader', async ({ type }, thunkAPI) => {
  try {
    const response = await axios.get<Array<ACSATAConfigType>>(
      `${process.env.REACT_APP_API_URL}/acs/portConfig?type=${type}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching configuration!' });
  }
});

export const getOrganizationModelsBundle = createAsyncThunk<
  OrganizationModelBundleType,
  { org_id: number },
  { state: RootReducerState }
>('inventory/getOrganizationModelsBundle', async ({ org_id }, thunkAPI) => {
  try {
    const response = await axios.get<OrganizationModelBundleType>(
      `${process.env.REACT_APP_API_URL}/inventory/organization-model-bundle?org_id=${org_id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching organization bundle!' });
  }
});

export const patchOrganizationModelsBundle = createAsyncThunk<
  { success: boolean; models: Array<{ id: number; isGlobal: boolean }> },
  {
    data: {
      organization: { id: number };
      models: Array<{ id: number; isGlobal: boolean }>;
    };
    id: number;
  },
  { state: RootReducerState }
>('inventory/patchOrganizationModelsBundle', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/organization-model-bundle/${id}`, data);

    const models = data.models.map(model => ({
      id: model.id,
      isGlobal: model.isGlobal || false,
    }));

    return { success: response.data.success, models };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating organization models!' });
  }
});

// Accessory Bundles

export const getAccessoryBundles = createAsyncThunk<
  AccessoryBundleType[],
  { search?: string },
  { state: RootReducerState }
>('inventory/getAccessoryBundles', async ({ search }, thunkAPI) => {
  try {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await axios.get<AccessoryBundleType[]>(
      `${process.env.REACT_APP_API_URL}/inventory/accessory-bundles${params}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching accessory bundles' });
  }
});

export const postAccessoryBundle = createAsyncThunk<
  AccessoryBundleType,
  {
    data: {
      name: string;
      description?: string;
      items: Array<{ modelId: number; count: number }>;
    };
  },
  { state: RootReducerState }
>('inventory/postAccessoryBundle', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AccessoryBundleType>(
      `${process.env.REACT_APP_API_URL}/inventory/accessory-bundles`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error creating accessory bundle' });
  }
});

export const patchAccessoryBundle = createAsyncThunk<
  AccessoryBundleType,
  {
    id: number;
    data: {
      name?: string;
      description?: string;
      items?: Array<{ modelId: number; count: number }>;
    };
  },
  { state: RootReducerState }
>('inventory/patchAccessoryBundle', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<AccessoryBundleType>(
      `${process.env.REACT_APP_API_URL}/inventory/accessory-bundles/${id}`,
      data
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error updating accessory bundle' });
  }
});

export const deleteAccessoryBundle = createAsyncThunk<AccessoryBundleType, { id: number }, { state: RootReducerState }>(
  'inventory/deleteAccessoryBundle',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<AccessoryBundleType>(
        `${process.env.REACT_APP_API_URL}/inventory/accessory-bundles/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error archiving accessory bundle' });
    }
  }
);

export const getInventoryDeviceDetails = createAsyncThunk<
  InventoryDeviceDetailsType,
  { deviceId: number },
  { state: RootReducerState }
>('inventory/getInventoryDeviceDetails', async ({ deviceId }, thunkAPI) => {
  try {
    const response = await axios.get<InventoryDeviceDetailsType>(
      `${process.env.REACT_APP_API_URL}/inventory/device/${deviceId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching device details!' });
  }
});

export const patchATAFirmware = createAsyncThunk<
  { success: boolean },
  {
    payload: {
      firmware?: { id: number; version: string };
      mcuFirmware?: { id: number; version: string };
    };
    deviceId: number;
  },
  { state: RootReducerState }
>('inventory/patchATAFirmware', async ({ payload, deviceId }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/acs-ata-device/${deviceId}/firmware`,
      payload
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating ATA details!' });
  }
});

export const patchDeviceDetails = createAsyncThunk<
  { success: boolean },
  {
    payload: {
      model?: { id: number };
      warehouse?: { id: number };
      description?: string;
      acsAta?: {
        imei?: string | null;
        deviceKey?: string;
        factoryPassword?: string | null;
        firmware?: { id: number; version: string };
        mcuFirmware?: { id: number; version: string };
        configOverrides?: Array<AcsAtaVirtualOverrideType>;
        ports?: Array<{
          fxsPort: number;
          portConfigOverrides: Array<AcsAtaVirtualOverrideType> | null;
        }>;
      };
      dialer?: {
        csAccount: string;
        dialerType: { id: number; displayName: string };
        csPrimaryReceiver: { number: string; countryCode: string } | null;
        csSecondaryReceiver: { number: string; countryCode: string } | null;
      };
      powerCycle?: {
        password: string;
      };
      router?: {
        imei: string;
      };
      smartphone?: {
        imei: string;
        imei2: string;
      };
      sim?: {
        eid: string;
        iccid: string;
        mobile: { number: string; countryCode: string } | null;
      };
    };
    deviceId: number;
  },
  { state: RootReducerState }
>('inventory/patchDeviceDetails', async ({ payload, deviceId }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/device/${deviceId}`,
      payload
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating ATA details!' });
  }
});

export const patchInventoryATAsFirmwares = createAsyncThunk<
  void,
  {
    payload: Array<{
      firmware?: null | AcsFirmwareType;
      mcuFirmware?: null | AcsFirmwareType;
      devices: number[];
      model: { id: number };
    }>;
  },
  { state: RootReducerState }
>('inventory/patchInventoryATAsFirmwares', async ({ payload }, thunkAPI) => {
  try {
    const response = await axios.patch<void>(
      `${process.env.REACT_APP_API_URL}/inventory/acs-ata-device/firmware`,
      payload
    );

    thunkAPI.dispatch(getDevicesList(thunkAPI.getState().inventoryReducer.devicesRouteParams));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating ATAs Firmwares!' });
  }
});

export const moveToInventory = createAsyncThunk<
  { success: boolean },
  {
    payload: {
      atas?: Array<{ id: number }>;
      batteries?: Array<{ id: number }>;
      enclosures?: Array<{ id: number }>;
      dialers?: Array<{ id: number }>;
      tcSims?: Array<{ id: number }>;
      smartphones?: Array<{ id: number }>;
      routers?: Array<{ id: number }>;
      powerCycles?: Array<{ id: number }>;
      warehouse: { id: number };
    };
  },
  { state: RootReducerState }
>('inventory/moveToInventory', async ({ payload }, thunkAPI) => {
  try {
    let url = `${process.env.REACT_APP_API_URL}/inventory/move-to-inventory`;
    if (payload.atas && payload?.atas?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/acs/move-to-inventory`;
    }

    if (payload.batteries && payload?.batteries?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/battery/move-to-inventory`;
    }

    if (payload.enclosures && payload?.enclosures?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/enclosures/move-to-inventory`;
    }

    if (payload.tcSims && payload?.tcSims?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/tc-sim/move-to-inventory`;
    }

    if (payload.routers && payload?.routers?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/routers/move-to-inventory`;
    }

    if (payload.dialers && payload?.dialers?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/dialer/move-to-inventory`;
    }

    if (payload.smartphones && payload?.smartphones?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/smartphones/move-to-inventory`;
    }

    if (payload.powerCycles && payload?.powerCycles?.length > 0) {
      url = `${process.env.REACT_APP_API_URL}/power-cycles/move-to-inventory`;
    }

    const response = await axios.patch<{ success: boolean }>(url, payload);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating Device!' });
  }
});

export const deleteDeviceDetails = createAsyncThunk<
  DeviceType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('inventory/deleteDeviceDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<DeviceType>(`${process.env.REACT_APP_API_URL}/inventory/device/${id}`);
    if (fetchList) {
      thunkAPI.dispatch(getDevicesList(thunkAPI.getState().inventoryReducer.devicesRouteParams));
    }
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting device!' });
  }
});

export const getFulfillmentOrdersList = createAsyncThunk<
  PaginationType<FulfillmentOrderType>,
  RouteParamsType,
  {
    state: RootReducerState;
  }
>('inventory/getFulfillmentOrdersList/no-loader', async (data, thunkAPI) => {
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      undefined,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );

    const mergedParams: Record<string, unknown> = { ...data, ...additionalParams };

    if (
      'status' in mergedParams &&
      (mergedParams.status === undefined ||
        mergedParams.status === null ||
        mergedParams.status === '' ||
        mergedParams.status === false)
    )
      delete mergedParams.status;
    if (
      'search' in mergedParams &&
      (mergedParams.search === undefined ||
        mergedParams.search === null ||
        mergedParams.search === '' ||
        mergedParams.search === false)
    )
      delete mergedParams.search;
    if (
      'from_warehouse_id' in mergedParams &&
      (mergedParams['from_warehouse_id'] === undefined ||
        mergedParams['from_warehouse_id'] === null ||
        mergedParams['from_warehouse_id'] === '' ||
        mergedParams['from_warehouse_id'] === false)
    )
      delete mergedParams['from_warehouse_id'];
    if (
      'to_warehouse_id' in mergedParams &&
      (mergedParams['to_warehouse_id'] === undefined ||
        mergedParams['to_warehouse_id'] === null ||
        mergedParams['to_warehouse_id'] === '' ||
        mergedParams['to_warehouse_id'] === false)
    )
      delete mergedParams['to_warehouse_id'];
    if ('q' in mergedParams) {
      const q = mergedParams.q;
      if (q !== undefined && q !== null && q !== '') {
        mergedParams.search = q;
      }
      delete mergedParams.q;
    }

    const queryString = Object.keys(mergedParams)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(String(mergedParams[key] ?? '')))
      .join('&');

    const response = await axios.get<PaginationType<FulfillmentOrderType>>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching orders!' });
  }
});

export const deleteFulfillmentOrderDetails = createAsyncThunk<
  FulfillmentOrderType,
  { id: number; fetchList: boolean },
  {
    state: RootReducerState;
  }
>('inventory/deleteFulfillmentOrderDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<FulfillmentOrderType>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}`
    );
    if (fetchList)
      thunkAPI.dispatch(getFulfillmentOrdersList(thunkAPI.getState().inventoryReducer.fulfillmentOrdersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting order!' });
  }
});

export const postFulfillmentOrderDetails = createAsyncThunk<
  FulfillmentOrderType,
  {
    data: {
      toWarehouse: { id: number };
      fromWarehouse: { id: number };
      items: Array<{ model: { id: number }; quantity: number }>;
      nonSerializedItems?: Array<{ model: { id: number }; count: number }>;
      accessoryBundles?: Array<{ bundle: { id: number }; quantity: number }>;
      ccNotifyUsers?: Array<{
        id: number;
        email: string;
        firstname: string;
        lastname: string;
      }>;
    };
  },
  { state: RootReducerState }
>('inventory/postFulfillmentOrderDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<FulfillmentOrderType>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment`,
      data
    );

    try {
      const routeParams = thunkAPI.getState().inventoryReducer.fulfillmentOrdersRouteParams;
      thunkAPI.dispatch(getFulfillmentOrdersList(routeParams));
    } catch (e: unknown) {
      console.error('Failed to refresh fulfillment orders list after creating order:', e);
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating order!' });
  }
});

export const postVirtualDevice = createAsyncThunk<
  { success: boolean },
  {
    data: {
      model: { id: number };
      order: { id: number };
      category: DeviceCategory;
    };
  },
  { state: RootReducerState }
>('inventory/postVirtualDevice', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/virtual-device`, data);

    thunkAPI.dispatch(getNewOrderDetails({ id: data.order.id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating virtual device!' });
  }
});

export const downloadInventoryAttachment = createAsyncThunk<
  string,
  {
    id: number;
  },
  { state: RootReducerState }
>('inventory/downloadInventoryAttachment', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<{ downloadUrl: string }>(
      `${process.env.REACT_APP_API_URL}/inventory/attachment/${id}`
    );

    window.open(response.data.downloadUrl, '_blank');

    return response.data.downloadUrl;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error downloading attachment!' });
  }
});

export const postImportDevicesToEquipmentGroup = createAsyncThunk<
  { success?: string[]; errors?: string[] },
  { data: { devices: Array<{ id: number }>; equipmentGroup: { id: number } } },
  { state: RootReducerState }
>('inventory/postImportDevicesToEquipmentGroup', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success?: string[];
      errors?: string[];
    }>(`${process.env.REACT_APP_API_URL}/inventory/import-device`, data);

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(getDevicesList(thunkAPI.getState().inventoryReducer.devicesRouteParams));
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during moving devices!' });
  }
});

export const postReplaceInventoryDevice = createAsyncThunk<
  { success: boolean },
  { data: { replacedDeviceId: number; deviceId: number; vid: string } },
  { state: RootReducerState }
>('inventory/postReplaceInventoryDevice', async ({ data }, thunkAPI) => {
  try {
    const { newOrderDetails } = thunkAPI.getState().ordersReducer;
    if (newOrderDetails) {
      await axios.post(`${process.env.REACT_APP_API_URL}/inventory/virtual-device/replace-device`, {
        ...data,
        orderId: newOrderDetails.id,
      });
      thunkAPI.dispatch(getNewOrderDetails({ id: newOrderDetails.id }));
    }
    return { success: true };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during replacing devices!' });
  }
});

export const patchImportDevicesToInventory = createAsyncThunk<
  { success?: string[]; errors?: string[] },
  { data: { devices: Array<{ id: number }>; warehouse: { id: number } } },
  { state: RootReducerState }
>('inventory/patchImportDevicesToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success?: string[];
      errors?: string[];
    }>(`${process.env.REACT_APP_API_URL}/inventory/update-devices-warehouse`, data);

    const handleModalClose = () => {
      thunkAPI.dispatch(setShowModal(false));
      thunkAPI.dispatch(setModalContent(null));
      thunkAPI.dispatch(getDevicesList(thunkAPI.getState().inventoryReducer.devicesRouteParams));
    };

    thunkAPI.dispatch(
      setModalContent({
        content: React.createElement(MoveDeviceSummary, {
          onClose: handleModalClose,
          errors: response.data.errors || [],
          success: response.data.success || [],
        }),
      })
    );
    thunkAPI.dispatch(setShowModal(true));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during moving devices!' });
  }
});

export const deleteVirtualDevice = createAsyncThunk<
  { success: boolean },
  { vid: string; order: { id: number } },
  {
    state: RootReducerState;
  }
>('inventory/deleteVirtualDevice', async ({ vid, order }, thunkAPI) => {
  try {
    const response = await axios.delete<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/virtual-device/${vid}`);

    thunkAPI.dispatch(getNewOrderDetails({ id: order.id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during removing virtual device!' });
  }
});

export const getFulfillmentOrderDetails = createAsyncThunk<
  FulfillmentOrderType,
  { id: number },
  { state: RootReducerState }
>('inventory/getFulfillmentOrderDetails', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<FulfillmentOrderType>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching order details!' });
  }
});

export const deleteFulfillmentOrderAttachment = createAsyncThunk<
  { success: boolean },
  { fulfillmentOrderId: number; attachmentId: number },
  { state: RootReducerState }
>('inventory/deleteFulfillmentOrderAttachment', async ({ fulfillmentOrderId, attachmentId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${fulfillmentOrderId}/attachments/${attachmentId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting attachment!' });
  }
});

export const deleteInventoryOrderAttachment = createAsyncThunk<
  { success: boolean },
  { orderId: number; attachmentId: number },
  { state: RootReducerState }
>('inventory/deleteInventoryOrderAttachment', async ({ orderId, attachmentId }, thunkAPI) => {
  try {
    const response = await axios.delete<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/order/${orderId}/attachments/${attachmentId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting attachment!' });
  }
});

export const patchFulfillmentOrderDetailsApprove = createAsyncThunk<
  { success: boolean },
  { data: null; id: number },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderDetailsApprove', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/approve`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order!' });
  }
});

export const patchFulfillmentOrderDetailsAssign = createAsyncThunk<
  { success: boolean },
  { data: { devices: Array<number> }; id: number },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderDetailsAssign', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/assign-devices`, data);

    try {
      const routeParams = thunkAPI.getState().inventoryReducer.fulfillmentOrdersRouteParams;
      thunkAPI.dispatch(getFulfillmentOrdersList(routeParams));
    } catch (e: unknown) {
      console.error('Failed to refresh fulfillment orders list after assigning devices:', e);
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order!' });
  }
});

export const patchFulfillmentOrderDetailsShipping = createAsyncThunk<
  { success: boolean },
  { data: Omit<InventoryShippingType, 'carrier'> & { carrier: { id: number } }; id: number },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderDetailsShipping', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/shipping`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order shipping details!' });
  }
});

export const patchFulfillmentOrderDetailsConfirmDelivery = createAsyncThunk<
  { success: boolean },
  { data: null; id: number },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderDetailsConfirmDelivery', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/confirm-delivery`, data);

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating order!' });
  }
});

export const getFulfillmentOrderLogs = createAsyncThunk<
  FulfillmentOrderLogType[],
  { id: number },
  { state: RootReducerState }
>('inventory/getFulfillmentOrderLogs', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.get<FulfillmentOrderLogType[]>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/logs`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch fulfillment order logs!' });
  }
});

export const getOrderLogs = createAsyncThunk<OrderLogType[], { id: number }, { state: RootReducerState }>(
  'inventory/getOrderLogs',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<OrderLogType[]>(`${process.env.REACT_APP_API_URL}/inventory/order/${id}/logs`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch order logs' });
    }
  }
);

export const patchFulfillmentOrderUpdateInvoiceStatus = createAsyncThunk<
  { success: boolean },
  { id: number; dto: { invoiceNumber: string; file: File | null } },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderUpdateInvoiceStatus', async ({ id, dto }, thunkAPI) => {
  try {
    const formData = new FormData();
    if (dto.file) {
      formData.append('file', dto.file);
    }
    formData.append('invoiceNumber', dto.invoiceNumber);

    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/invoice-created`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating invoice status!' });
  }
});

export const patchFulfillmentOrderUpdatePaymentStatus = createAsyncThunk<
  { success: boolean },
  { id: number; dto?: { paymentNumber?: string } },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderUpdatePaymentStatus', async ({ id, dto }, thunkAPI) => {
  try {
    const response = await axios.patch<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/payment-received`, dto || {});

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating payment status!' });
  }
});

export const acceptFulfillmentOrder = createAsyncThunk<
  { success: boolean },
  { id: number },
  { state: RootReducerState }
>('inventory/acceptFulfillmentOrder', async ({ id }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/accept`
    );

    thunkAPI.dispatch(getFulfillmentOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during accepting order!' });
  }
});

export const updateFulfillmentOrderWarehouse = createAsyncThunk<
  { success: boolean },
  { id: number; fromWarehouse: { id: number } },
  { state: RootReducerState }
>('inventory/updateFulfillmentOrderWarehouse', async ({ id, fromWarehouse }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}`,
      { fromWarehouse }
    );

    thunkAPI.dispatch(getFulfillmentOrderDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating warehouse!' });
  }
});

export const patchFulfillmentOrderItems = createAsyncThunk<
  { success: boolean },
  {
    id: number;
    data: {
      items?: Array<{ model: { id: number }; quantity: number }>;
      nonSerializedItems?: Array<{ model: { id: number }; count: number }>;
      accessoryBundles?: Array<{ bundle: { id: number }; quantity: number }>;
    };
  },
  { state: RootReducerState }
>('inventory/patchFulfillmentOrderItems', async ({ id, data }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}`,
      data
    );

    thunkAPI.dispatch(getFulfillmentOrderDetails({ id }));

    try {
      const routeParams = thunkAPI.getState().inventoryReducer.fulfillmentOrdersRouteParams;
      thunkAPI.dispatch(getFulfillmentOrdersList(routeParams));
    } catch (e: unknown) {
      console.error('Failed to refresh fulfillment orders list after updating order items:', e);
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating fulfillment order items!' });
  }
});

export const postOrderComment = createAsyncThunk<
  CommentType,
  { data: CreateOrderCommentDto },
  { state: RootReducerState }
>('inventory/postOrderComment', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<CommentType>(`${process.env.REACT_APP_API_URL}/inventory/comment`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating order comment!' });
  }
});

export const cancelFulfillmentOrderDetails = createAsyncThunk<
  FulfillmentOrderType,
  { id: number; fetchList: boolean },
  {
    state: RootReducerState;
  }
>('inventory/cancelFulfillmentOrderDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.patch<FulfillmentOrderType>(
      `${process.env.REACT_APP_API_URL}/inventory/fulfillment/${id}/cancel`
    );
    if (fetchList)
      thunkAPI.dispatch(getFulfillmentOrdersList(thunkAPI.getState().inventoryReducer.fulfillmentOrdersRouteParams));
    thunkAPI.dispatch(getFulfillmentOrderDetails({ id }));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during canceling order!' });
  }
});

export const postAddNonSerializedItemsToInventory = createAsyncThunk<
  NonSerializedItemType,
  {
    data: AddNonSerializedItemsToInventoryDto;
  },
  { state: RootReducerState }
>('inventory/postAddNonSerializedItemsToInventory', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<NonSerializedItemType>(
      `${process.env.REACT_APP_API_URL}/inventory/non-serialized-items`,
      data
    );

    const warehouseId = thunkAPI.getState().warehousesReducer?.microserviceWarehouseDetails?.id;
    if (warehouseId) {
      thunkAPI.dispatch(getInventoryList({ warehouse_id: warehouseId }));
      thunkAPI.dispatch(getNonSerializedItemsByWarehouse({ warehouseId }));
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during adding non-serialized items!' });
  }
});

export const getNonSerializedItemsByWarehouse = createAsyncThunk<
  Array<NonSerializedItemType>,
  { warehouseId: number },
  { state: RootReducerState }
>('inventory/getNonSerializedItemsByWarehouse', async ({ warehouseId }, thunkAPI) => {
  try {
    const response = await axios.get<{ items: Array<NonSerializedItemType> }>(
      `${process.env.REACT_APP_API_URL}/inventory/non-serialized-items/warehouse/${warehouseId}`
    );

    return response.data.items;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching non-serialized items' });
  }
});

export const getNonSerializedItemLogs = createAsyncThunk<
  Array<NonSerializedItemLogType>,
  { itemId: number },
  { state: RootReducerState }
>('inventory/getNonSerializedItemLogs', async ({ itemId }, thunkAPI) => {
  try {
    const response = await axios.get<{ items: Array<NonSerializedItemLogType> }>(
      `${process.env.REACT_APP_API_URL}/inventory/non-serialized/${itemId}/logs`
    );

    return response.data.items;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching non-serialized item logs' });
  }
});

export const getNonSerializedItemsReport = createAsyncThunk<
  Array<NonSerializedItemType>,
  { warehouseIds: number[] },
  { state: RootReducerState }
>('inventory/getNonSerializedItemsReport', async ({ warehouseIds }, thunkAPI) => {
  try {
    if (!warehouseIds || warehouseIds.length === 0) {
      return [];
    }

    const responses = await Promise.all(
      warehouseIds.map(id =>
        axios.get<{ items: Array<NonSerializedItemType> }>(
          `${process.env.REACT_APP_API_URL}/inventory/non-serialized-items/warehouse/${id}`
        )
      )
    );

    const allItems = responses.flatMap(response => response.data?.items || []);

    return allItems;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error fetching non-serialized items' });
  }
});

export const postInventoryDeviceOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('enclosures/postInventoryDeviceOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/inventory/device/${id}/ownership-transfer`, data);

    thunkAPI.dispatch(getInventoryDeviceDetails({ deviceId: id }));
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Device ownership transfer!' });
  }
});

export const startDeviceQAcheck = createAsyncThunk<
  { success: boolean },
  { deviceId: number },
  { state: RootReducerState }
>('inventory/startDeviceQAcheck', async ({ deviceId }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/device/${deviceId}/start-qa`,
      {}
    );

    const { inventoryDeviceDetails } = thunkAPI.getState().inventoryReducer;
    const { user } = thunkAPI.getState().authReducer;
    if (inventoryDeviceDetails) {
      thunkAPI.dispatch(
        setInventoryDeviceDetails({
          ...inventoryDeviceDetails,
          qaCheckBy: user,
          deviceStatus: DeviceStatus.ReturnedQACheck,
        })
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating device details!' });
  }
});

export const finishDeviceQAcheck = createAsyncThunk<
  { success: boolean },
  { deviceId: number; paylaod: { failReason?: string; passReason?: string } },
  { state: RootReducerState }
>('inventory/finishDeviceQAcheck', async ({ deviceId, paylaod }, thunkAPI) => {
  try {
    const response = await axios.patch<{ success: boolean }>(
      `${process.env.REACT_APP_API_URL}/inventory/device/${deviceId}/finish-qa`,
      paylaod
    );

    const { inventoryDeviceDetails } = thunkAPI.getState().inventoryReducer;
    if (inventoryDeviceDetails) {
      thunkAPI.dispatch(
        setInventoryDeviceDetails({
          ...inventoryDeviceDetails,
          qaNote: paylaod.failReason || paylaod.passReason,
          deviceStatus: paylaod.failReason ? DeviceStatus.OutOfService : DeviceStatus.Refurbished,
        })
      );
    }

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating device details!' });
  }
});
